/**
 * Customer-facing presentation of a purchase request (PSM `buy_requests`).
 *
 * One table drives the /orders cards, the detail page and (later) emails, so a
 * state always reads the same way: label, what is happening, what the customer
 * has to do next, which purchase step it sits on, and which filter tab it
 * belongs to.
 *
 * PSM states: submitted → under_review → approved | rejected | withdrawn
 * (approved / rejected / withdrawn are terminal in PSM). "approved" means the
 * purchase terms were confirmed — NOT that the vehicle is secured — so its copy
 * says so explicitly and asks for the payment step. Deadlines are not stored
 * anywhere yet, so none is shown rather than inventing one.
 */
import { parsePortalTime } from './portalTime';

export type PurchaseRequestGroup = 'action' | 'progress' | 'done';

export interface PurchaseRequestStatusView {
  label: string;
  description: string;
  nextAction: string;
  group: PurchaseRequestGroup;
  /** 1-based index into PURCHASE_STEPS of the step in progress. */
  currentStep: number;
  /** Set when the request ended without a purchase. */
  outcome: 'not_purchased' | 'withdrawn' | null;
  tone: 'info' | 'action' | 'success' | 'neutral' | 'danger';
}

export const PURCHASE_STEPS: { title: string; description: string }[] = [
  { title: 'Request received', description: 'We have received your purchase request.' },
  {
    title: 'Vehicle & terms check',
    description: 'We check the vehicle’s availability and the purchase terms.',
  },
  { title: 'Payment', description: 'We confirm the payment required to purchase the vehicle.' },
  { title: 'Purchase', description: 'We complete the purchase of the vehicle for you.' },
  {
    title: 'Result',
    description: 'The vehicle is purchased, or we tell you why it could not be.',
  },
];

const STATUS: Record<string, PurchaseRequestStatusView> = {
  submitted: {
    label: 'Request received',
    description: 'We have received your purchase request and will start checking the vehicle.',
    nextAction: 'No action needed right now. We will email you when we need anything from you.',
    group: 'progress',
    currentStep: 2,
    outcome: null,
    tone: 'info',
  },
  under_review: {
    label: 'Checking vehicle & terms',
    description: 'We are confirming that the vehicle is available and checking the purchase terms.',
    nextAction: 'No action needed right now. We will email you when we need anything from you.',
    group: 'progress',
    currentStep: 2,
    outcome: null,
    tone: 'info',
  },
  approved: {
    label: 'Payment required',
    description:
      'The purchase terms are confirmed. The vehicle is not secured yet — it is purchased after your payment is confirmed.',
    nextAction:
      'Check the invoice and payment instructions from Provide Cars, then complete the payment.',
    group: 'action',
    currentStep: 3,
    outcome: null,
    tone: 'action',
  },
  rejected: {
    label: 'Could not be purchased',
    description: 'We were unable to purchase this vehicle for you.',
    nextAction:
      'If you have already paid for this request, we will contact you about the refund. Please contact us if you have any questions.',
    group: 'done',
    currentStep: 5,
    outcome: 'not_purchased',
    tone: 'danger',
  },
  withdrawn: {
    label: 'Request withdrawn',
    description: 'This purchase request was withdrawn.',
    nextAction: 'If you have already paid for this request, we will contact you about the refund.',
    group: 'done',
    currentStep: 5,
    outcome: 'withdrawn',
    tone: 'neutral',
  },
};

const UNKNOWN: PurchaseRequestStatusView = {
  label: 'In progress',
  description: 'We are processing your purchase request.',
  nextAction: 'No action needed right now. We will email you when we need anything from you.',
  group: 'progress',
  currentStep: 2,
  outcome: null,
  tone: 'info',
};

export function purchaseRequestStatus(state: string | null | undefined): PurchaseRequestStatusView {
  return (state && STATUS[state]) || UNKNOWN;
}

const REJECT_REASONS: Record<string, string> = {
  price_mismatch: 'The purchase price could not be agreed.',
  vehicle_unavailable: 'The vehicle is no longer available.',
  credit_concern: 'We could not confirm the purchase conditions for this request.',
  delivery_restricted: 'We cannot deliver this vehicle to your destination.',
  customer_request: 'The request was cancelled at your request.',
  other: 'Please contact us for details.',
};

/** Customer-safe reason text for a PSM reject_reason code (never the raw code). */
export function rejectReasonText(code: string | null | undefined): string | null {
  if (!code) return null;
  return REJECT_REASONS[code] ?? REJECT_REASONS.other;
}

export const GROUP_LABELS: Record<PurchaseRequestGroup | 'all', string> = {
  all: 'All',
  action: 'Action needed',
  progress: 'In progress',
  done: 'Completed',
};

const GROUP_ORDER: Record<PurchaseRequestGroup, number> = { action: 0, progress: 1, done: 2 };

/** Action-needed first, then in progress, then completed; newest first inside each. */
export function sortPurchaseRequests<T extends { state: string; createdAt: string }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const g =
      GROUP_ORDER[purchaseRequestStatus(a.state).group] -
      GROUP_ORDER[purchaseRequestStatus(b.state).group];
    if (g !== 0) return g;
    return (b.createdAt || '').localeCompare(a.createdAt || '');
  });
}

/** Display reference shown to the customer, e.g. REQ-000123. */
export function requestNumber(id: number): string {
  return `REQ-${String(id).padStart(6, '0')}`;
}

export function vehicleTitle(v: {
  vehicleMakerEnSnapshot: string | null;
  vehicleCarnameEnSnapshot: string | null;
}): string {
  const name = [v.vehicleMakerEnSnapshot, v.vehicleCarnameEnSnapshot]
    .filter((p): p is string => !!p && p.trim() !== '')
    .join(' ');
  return name || 'Vehicle';
}

// AUCNET STOCK writes a system line into notes ("AUCNET STOCK buy request (...")
// for staff; it is not something the customer typed, so it is never shown.
const SYSTEM_NOTES_PREFIX = 'AUCNET STOCK buy request (';

export function customerNotes(notes: string | null | undefined): string | null {
  const text = notes?.trim();
  if (!text || text.startsWith(SYSTEM_NOTES_PREFIX)) return null;
  return text;
}

/** "13 Oct 2026" (Japan time — the dates staff work in). */
export function formatRequestDate(iso: string | null | undefined): string {
  const d = parsePortalTime(iso);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Tokyo',
  });
}

/** "13 Oct 2026, 13:28 JST". */
export function formatRequestDateTime(iso: string | null | undefined): string {
  const d = parsePortalTime(iso);
  if (!d) return '—';
  const text = d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Tokyo',
  });
  return `${text} JST`;
}

export function formatJpy(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return `¥${amount.toLocaleString('en-US')}`;
}
